import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { createUserWithEmailAndPassword, signInWithPopup, GoogleAuthProvider } from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';
import { auth, firestore } from './firebase/config';

const BusinessUserSignUp = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  // User Account Info (Step 1)
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Manager/Admin Profile Info (Step 2)
  const [managerProfile, setManagerProfile] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    position: 'Owner'
  });

  // Step 1: Create User Account
  const handleCreateAccount = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Create basic user document
      await setDoc(doc(firestore, 'users', user.uid), {
        email: user.email,
        role: 'business_user',
        createdAt: new Date().toISOString(),
      });

      console.log('Business user account created:', user.uid);
      setCurrentStep(2);
    } catch (error) {
      console.error('Account creation error:', error);
      setError('Failed to create account. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Complete Manager Profile and Go to Dashboard
  const handleManagerProfile = async (e) => {
    e.preventDefault();
    setError('');

    if (!managerProfile.firstName || !managerProfile.lastName) {
      setError('Please fill in all required fields');
      return;
    }

    setLoading(true);

    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      // Update user document with manager profile
      await setDoc(doc(firestore, 'users', user.uid), {
        email: user.email,
        role: 'business_user',
        firstName: managerProfile.firstName,
        lastName: managerProfile.lastName,
        phone: managerProfile.phone,
        position: managerProfile.position,
        isBusinessProfileComplete: false,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });

      // Create manager profile in userProfiles collection
      await setDoc(doc(firestore, 'userProfiles', user.uid), {
        firstName: managerProfile.firstName,
        lastName: managerProfile.lastName,
        phone: managerProfile.phone,
        position: managerProfile.position,
        isBusinessAdmin: true,
        isBusinessProfileComplete: false,
        createdAt: new Date().toISOString()
      });

      console.log('Manager profile completed successfully');
      navigate('/business-dashboard');
    } catch (error) {
      console.error('Manager profile error:', error);
      setError('Failed to save manager profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Google Sign Up
  const handleGoogleSignUp = async () => {
    setError('');
    setLoading(true);

    try {
      const provider = new GoogleAuthProvider();
      const userCredential = await signInWithPopup(auth, provider);
      const user = userCredential.user;

      // Create user document with Google info
      await setDoc(doc(firestore, 'users', user.uid), {
        email: user.email,
        firstName: user.displayName?.split(' ')[0] || 'Business',
        lastName: user.displayName?.split(' ').slice(1).join(' ') || 'User',
        role: 'business_user',
        position: 'Owner',
        isBusinessProfileComplete: false,
        createdAt: new Date().toISOString()
      });

      // Create user profile
      await setDoc(doc(firestore, 'userProfiles', user.uid), {
        firstName: user.displayName?.split(' ')[0] || 'Business',
        lastName: user.displayName?.split(' ').slice(1).join(' ') || 'User',
        position: 'Owner',
        isBusinessAdmin: true,
        isBusinessProfileComplete: false,
        createdAt: new Date().toISOString()
      });

      console.log('Google business signup successful');
      navigate('/business-dashboard');
    } catch (error) {
      console.error('Google signup error:', error);
      setError('Failed to sign up with Google.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    navigate('/');
  };

  return (
    <div className="relative flex items-center justify-center min-h-screen w-full overflow-hidden bg-gradient-to-br from-orange-50 to-orange-100">
      {/* Background pattern */}
      <div className="absolute inset-0 grid grid-cols-4 grid-rows-3 gap-4 z-0">
        {Array(12).fill().map((_, i) => (
          <div key={i} className="rounded-lg bg-gradient-to-br from-orange-200 to-orange-100 opacity-50"></div>
        ))}
      </div>
      
      {/* Main container */}
      <div className="relative z-10 flex flex-row w-full max-w-4xl bg-white rounded-lg shadow-xl overflow-hidden">
        
        {/* Form side */}
        <div className="w-full md:w-1/2 p-8 md:p-12">
          <div className="flex items-center mb-8">
            <img 
              src="/asset/Logo/hireeLogo.png" 
              alt="Hiree Logo" 
              className="h-10 w-auto"
            />
            <span className="ml-3 text-xl text-gray-600">
              {currentStep === 1 && 'Create your business account'}
              {currentStep === 2 && 'Tell us about yourself'}
            </span>
          </div>
          
          {error && (
            <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md text-sm">
              {error}
            </div>
          )}

          <form onSubmit={currentStep === 1 ? handleCreateAccount : handleManagerProfile} className="space-y-6">
            
            {/* Step 1: Account Creation */}
            {currentStep === 1 && (
              <>
                <div className="space-y-2">
                  <label htmlFor="email" className="block text-left text-gray-700">
                    Email Address<span className="text-orange-500">*</span>
                  </label>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your business email"
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-400"
                  />
                </div>
                
                <div className="space-y-2">
                  <label htmlFor="password" className="block text-left text-gray-700">
                    Password<span className="text-orange-500">*</span>
                  </label>
                  <input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Create a password"
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-400"
                  />
                  <p className="text-xs text-gray-500">Password must be at least 6 characters</p>
                </div>
                
                <div className="space-y-2">
                  <label htmlFor="confirm-password" className="block text-left text-gray-700">
                    Confirm Password<span className="text-orange-500">*</span>
                  </label>
                  <input
                    id="confirm-password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm your password"
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-400"
                  />
                </div>
                
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3 px-4 bg-gradient-to-r from-orange-400 to-orange-500 text-white rounded-md hover:from-orange-500 hover:to-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:ring-opacity-50 disabled:opacity-70"
                >
                  {loading ? 'Creating Account...' : 'Continue'}
                </button>
              
                <div className="relative flex items-center justify-center my-4">
                  <div className="border-t border-gray-300 w-full"></div>
                  <span className="bg-white px-3 text-sm text-gray-500 absolute">or</span>
                </div>
                
                <button
                  type="button"
                  onClick={handleGoogleSignUp}
                  disabled={loading}
                  className="w-full py-3 border border-gray-300 rounded-md flex items-center justify-center space-x-2 hover:bg-gray-50 transition-colors disabled:opacity-70"
                >
                  <svg className="h-5 w-5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                  </svg>
                  <span>Continue with Google</span>
                </button>
                
                <div className="text-center mt-6">
                  <p className="text-sm text-gray-600">
                    Already have an account?{' '}
                    <Link to="/login" className="text-orange-500 hover:text-orange-600">
                      Sign in
                    </Link>
                  </p>
                </div>
              </>
            )}
            
            {/* Step 2: Manager Profile */}
            {currentStep === 2 && (
              <>
                <div className="text-center mb-4">
                  <p className="text-sm text-gray-600">You can add business details later in your dashboard</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label htmlFor="firstName" className="block text-left text-gray-700">
                      First Name<span className="text-orange-500">*</span>
                    </label>
                    <input
                      id="firstName"
                      type="text"
                      value={managerProfile.firstName}
                      onChange={(e) => setManagerProfile({...managerProfile, firstName: e.target.value})}
                      placeholder="Your first name"
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-400"
                    />
                  </div>
                  <div className="space-y-2">
                    <label htmlFor="lastName" className="block text-left text-gray-700">
                      Last Name<span className="text-orange-500">*</span>
                    </label>
                    <input
                      id="lastName"
                      type="text"
                      value={managerProfile.lastName}
                      onChange={(e) => setManagerProfile({...managerProfile, lastName: e.target.value})}
                      placeholder="Your last name"
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-400"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label htmlFor="phone" className="block text-left text-gray-700">
                    Phone Number
                  </label>
                  <input
                    id="phone"
                    type="tel"
                    value={managerProfile.phone}
                    onChange={(e) => setManagerProfile({...managerProfile, phone: e.target.value})}
                    placeholder="Your phone number"
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-400"
                  />
                </div>

                <div className="space-y-2">
                  <label htmlFor="position" className="block text-left text-gray-700">
                    Your Position
                  </label>
                  <select
                    id="position"
                    value={managerProfile.position}
                    onChange={(e) => setManagerProfile({...managerProfile, position: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-400"
                  >
                    <option value="Owner">Owner</option>
                    <option value="Manager">Manager</option>
                    <option value="Director">Director</option>
                    <option value="Administrator">Administrator</option>
                    <option value="HR Manager">HR Manager</option>
                  </select>
                </div>

                <div className="flex space-x-4">
                  <button
                    type="button"
                    onClick={() => setCurrentStep(1)}
                    className="flex-1 py-3 border border-orange-500 text-orange-500 rounded-md hover:bg-orange-50 transition-colors"
                  >
                    Back
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex-1 py-3 px-4 bg-gradient-to-r from-orange-400 to-orange-500 text-white rounded-md hover:from-orange-500 hover:to-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:ring-opacity-50 disabled:opacity-70"
                  >
                    {loading ? 'Completing...' : 'Complete Setup'}
                  </button>
                </div>
              </>
            )}
          </form>
        </div>
        
        {/* Video side - only shown on medium screens and larger */}
        <div className="hidden md:block md:w-1/2 relative">
          <button 
            onClick={handleClose}
            className="absolute top-4 right-4 z-20 p-2 bg-white bg-opacity-70 rounded-full hover:bg-opacity-100"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <video autoPlay muted loop className="absolute inset-0 w-full h-full object-cover">
            <source src="/asset/SignInPage/video1.mp4" type="video/mp4" />
            Your browser does not support the video tag.
          </video>
          <div className="absolute inset-0 bg-black bg-opacity-30"></div>
          <div className="absolute bottom-8 left-8 z-20 text-white">
            <h2 className="text-2xl font-bold mb-2">Join Hiree for Business</h2>
            <p className="text-sm opacity-90">Find and hire the best talent for your restaurant</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusinessUserSignUp;
